import {
  buildCoverPage,
  buildSectionTitle,
  buildParagraph,
  buildTable,
  buildDocument,
  downloadDocx,
} from "./docxReportGenerator.js";

const LABELS = {
  fr: {
    reportTitle: "Rapport d'Analyse de Risques",
    coverSubtitle: "Atelier 3 - Mesures de Sécurité",
    defaultOrg: "Organisation",
    defaultAnalysis: "Analyse EBIOS RM",
    section1Title: "1. MESURES DE SÉCURITÉ",
    categories: {
      governance: "GOUVERNANCE",
      protection: "PROTECTION",
      defense: "DÉFENSE",
      resilience: "RÉSILIENCE",
      other: "AUTRE",
    },
    statuses: {
      planned: "Planifiée",
      "in-progress": "En cours",
      implemented: "Implémentée",
      abandoned: "Abandonnée",
    },
    emptyMeasures: "Aucune mesure de sécurité identifiée.",
    headers: [
      "Titre / Description",
      "Catégorie",
      "Statut",
      "Détails",
      "Chemins d'attaque",
    ],
    deadlineLabel: "Échéance:",
    effectivenessLabel: "Efficacité:",
    sourceUnknown: "Source inconnue",
    noPaths: "Aucun",
    categoryFallback: "AUTRE",
    statusFallback: "Non défini",
    section2Title: "2. RISQUE RÉSIDUEL PAR PARTIE PRENANTE",
    emptyRisk: "Aucune donnée de risque résiduel disponible.",
    riskInfo:
      "Le risque initial est calculé en Activité 1. Le risque résiduel est recalculé après application des mesures de sécurité.",
    riskHeaders: [
      "Partie Prenante",
      "Risque Initial",
      "Ancienne Zone",
      "Risque Résiduel",
      "Expression du risque résiduel",
      "Nouvelle Zone",
      "Réduction (%)",
    ],
    docTitle: "Atelier 3 - Mesures de Sécurité",
    footer: "EBIOS RM - Mesures Écosystème",
    fileBase: "EBIOS-RM_Mesures-Ecosysteme",
  },
  en: {
    reportTitle: "Risk Analysis Report",
    coverSubtitle: "Workshop 3 - Security Measures",
    defaultOrg: "Organization",
    defaultAnalysis: "EBIOS RM Analysis",
    section1Title: "1. SECURITY MEASURES",
    categories: {
      governance: "GOVERNANCE",
      protection: "PROTECTION",
      defense: "DEFENSE",
      resilience: "RESILIENCE",
      other: "OTHER",
    },
    statuses: {
      planned: "Planned",
      "in-progress": "In progress",
      implemented: "Implemented",
      abandoned: "Abandoned",
    },
    emptyMeasures: "No security measures identified.",
    headers: [
      "Title / Description",
      "Category",
      "Status",
      "Details",
      "Attack Paths",
    ],
    deadlineLabel: "Deadline:",
    effectivenessLabel: "Effectiveness:",
    sourceUnknown: "Unknown Source",
    noPaths: "None",
    categoryFallback: "OTHER",
    statusFallback: "Not defined",
    section2Title: "2. RESIDUAL RISK PER STAKEHOLDER",
    emptyRisk: "No residual risk data available.",
    riskInfo:
      "Initial risk is calculated in Workshop 1. Residual risk is recalculated after applying security measures.",
    riskHeaders: [
      "Stakeholder",
      "Initial Risk",
      "Old Zone",
      "Residual Risk",
      "Residual Risk Expression",
      "New Zone",
      "Reduction (%)",
    ],
    docTitle: "Workshop 3 - Security Measures",
    footer: "EBIOS RM - Ecosystem Measures",
    fileBase: "EBIOS-RM_Ecosystem-Measures",
  },
};

export async function generateEcosystemMeasuresDocx(
  reportData = {},
  analysisData = {},
  lang = "fr",
) {
  const L = LABELS[lang] || LABELS.fr;
  const sections = [];

  // Cover Page
  sections.push(
    buildCoverPage(
      L.reportTitle,
      L.coverSubtitle,
      analysisData?.companyName || L.defaultOrg,
      analysisData?.name || L.defaultAnalysis,
    ),
  );

  // Section 1: Measures
  const measuresSection = [];
  measuresSection.push(buildSectionTitle(L.section1Title, 1));

  const measures = reportData.measures || [];

  if (measures.length === 0) {
    measuresSection.push(buildParagraph(L.emptyMeasures));
  } else {
    const rows = measures.map((measure) => {
      const details = [];
      if (measure.responsibleParty)
        details.push(`Resp: ${measure.responsibleParty}`);
      if (measure.deadline)
        details.push(
          `${L.deadlineLabel} ${new Date(measure.deadline).toLocaleDateString(lang === "en" ? "en-GB" : undefined)}`,
        );

      const eff = [];
      if (Number(measure.dependanceEffectiveness) > 0)
        eff.push(`D-${Number(measure.dependanceEffectiveness).toFixed(1)}`);
      if (Number(measure.penetrationEffectiveness) > 0)
        eff.push(`P-${Number(measure.penetrationEffectiveness).toFixed(1)}`);
      if (Number(measure.maturiteCyberEffectiveness) > 0)
        eff.push(`M+${Number(measure.maturiteCyberEffectiveness).toFixed(1)}`);
      if (Number(measure.confianceEffectiveness) > 0)
        eff.push(`C+${Number(measure.confianceEffectiveness).toFixed(1)}`);

      if (eff.length > 0) details.push(`${L.effectivenessLabel} ${eff.join(", ")}`);
      const detailsText = details.join("\n");

      const pathsText =
        measure.attackPathsDetails && measure.attackPathsDetails.length > 0
          ? measure.attackPathsDetails
              .map(
                (p) =>
                  `[${p.referenceCode}] ${p.sourceRiskName || L.sourceUnknown}`,
              )
              .join("\n")
          : L.noPaths;

      return [
        `${measure.title || ""}\n${measure.description || ""}`,
        L.categories[measure.category] || measure.category || L.categoryFallback,
        L.statuses[measure.implementation] ||
          measure.implementation ||
          L.statusFallback,
        detailsText,
        pathsText,
      ];
    });

    measuresSection.push(
      buildTable(L.headers, rows, [4500, 2500, 2500, 2500, 2950], 14950),
    );
  }
  sections.push(measuresSection);

  // Section 2: Residual Risk
  const riskSection = [];
  riskSection.push(buildSectionTitle(L.section2Title, 1));

  const residualRisks = reportData.residualRisks || [];
  if (residualRisks.length === 0) {
    riskSection.push(buildParagraph(L.emptyRisk));
  } else {
    riskSection.push(buildParagraph(L.riskInfo));

    const riskRows = residualRisks.map((r) => [
      r.stakeholderName || "",
      r.initialRisk.toFixed(2),
      r.initialZone || "\u2014",
      (Number(r.residualRisk) || 0).toFixed(2),
      r.residualRiskExpression || "",
      r.residualZone || "\u2014",
      `-${r.reduction}%`,
    ]);

    riskSection.push(
      buildTable(
        L.riskHeaders,
        riskRows,
        [2500, 1500, 1800, 1500, 2500, 1800, 1350],
        12950,
      ),
    );
  }
  sections.push(riskSection);

  let watermarkImage = null;
  try {
    const response = await fetch("/watermark_page-0001.jpg");
    if (response.ok) {
      const arrayBuffer = await response.arrayBuffer();
      watermarkImage = new Uint8Array(arrayBuffer);
    }
  } catch (e) {
    console.warn("Could not load watermark image", e);
  }

  const doc = buildDocument(
    L.docTitle,
    sections,
    L.footer,
    watermarkImage,
    { orientation: "landscape" },
  );

  const ts = new Date().toISOString().slice(0, 10);
  await downloadDocx(doc, `${L.fileBase}_${ts}${lang === "en" ? "_EN" : ""}`);
}
